import { useState } from "react";
import { Plus, Check, Lock } from "lucide-react";
import { useApi, useMutation } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import Card from "../components/Card";
import Table from "../components/Table";
import Modal from "../components/Modal";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Select2 from "../components/Select2";
import Badge from "../components/Badge";
import Alert from "../components/Alert";
import PageHeader from "../components/PageHeader";
import SearchBar from "../components/SearchBar";

/**
 * Generic master CRUD page — backed by real API, permission-aware.
 *
 * Props:
 *   title, subtitle, breadcrumb
 *   module         : backend PageModule enum value (e.g. "COLOR", "UOM").  When
 *                    present, per-action gating uses hasPermission(module, X)
 *                    so non-admin roles with explicit grants can manage data.
 *                    Falls back to canManage() (role-only) when omitted, for
 *                    backwards compat.
 *   api            : { getAll, create, update, toggle, delete }
 *   cols           : Array<{ key, label, dim?, render? }>
 *   formFields     : Array<{ key, label, placeholder?, required?, type?, full?, options?, hint? }>
 *   entityName     : string
 */
export default function SimpleMaster({
  title, subtitle, breadcrumb, api, cols, formFields, entityName, module,
}) {
  // Phase 36da.99.ch — Granular per-action permission gating.  Before,
  // SimpleMaster used canManage() which is a ROLE check (only
  // SUPER_ADMIN/COMPANY_OWNER/COMPANY_ADMIN pass).  Backend, however,
  // uses hasPermission(module, action) which honors user-level grants
  // from user_page_access — so a LOCATION_MANAGER who was granted
  // COLOR/CREATE could be rejected on the frontend (no Add button)
  // but accepted on the backend, and vice-versa for COMPANY_ADMIN
  // without an explicit grant.  Mirroring the backend's check here
  // fixes both directions.  When `module` isn't supplied (legacy
  // usage), fall back to the role check so nothing regresses.
  const { canManage, hasPermission } = useAuth();
  const fallbackManage = canManage();
  const canCreate = module ? hasPermission(module, "CREATE") : fallbackManage;
  const canEdit = module ? hasPermission(module, "EDIT") : fallbackManage;
  const canDelete = module ? hasPermission(module, "DELETE") : fallbackManage;

  const { data, loading, refetch } = useApi(api.getAll);
  const { mutate, loading: saving, error: saveError, clearError } = useMutation();

  const emptyForm = Object.fromEntries(formFields.map(f => [f.key, f.defaultValue ?? ""]));
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [localError, setLocalError] = useState("");

  const list = Array.isArray(data) ? data : [];
  const filtered = list.filter(row =>
    Object.values(row).some(v => String(v ?? "").toLowerCase().includes(search.toLowerCase()))
  );

  const setField = key => val => setForm(p => ({ ...p, [key]: val }));

  const openAdd = () => {
    setEditing(null); setForm(emptyForm);
    clearError(); setLocalError(""); setModal(true);
  };
  const openEdit = row => {
    setEditing(row); setForm({ ...emptyForm, ...row });
    clearError(); setLocalError(""); setModal(true);
  };

  const handleDelete = row => mutate(() => api.delete(row.id), { onSuccess: refetch });
  const handleToggle = row => mutate(() => api.toggle(row.id), { onSuccess: refetch });

  const handleSave = async () => {
    const firstReq = formFields.find(f => f.required);
    if (firstReq && !String(form[firstReq.key] || "").trim()) {
      setLocalError(`${firstReq.label} is required`);
      return;
    }
    await mutate(
      () => editing ? api.update(editing.id, form) : api.create(form),
      { onSuccess: () => { setModal(false); refetch(); } }
    );
  };

  const tableColumns = [
    ...cols,
    { key: "isActive", label: "Status", render: v => <Badge label={v ? "ACTIVE" : "INACTIVE"} /> },
  ];

  const entity = entityName || title.split(" ")[0];

  return (
    <div className="fade-up" style={{ padding: "20px 24px" }}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb || ["Masters", title]}
        actions={<>
          <SearchBar value={search} onChange={setSearch} />
          {canCreate
            ? (
              <Btn variant="primary" size="sm" icon={<Plus size={13} />} onClick={openAdd}>
                Add {entity}
              </Btn>
            ) : (
              <Btn variant="secondary" size="sm" icon={<Lock size={13} />} disabled>
                View Only
              </Btn>
            )
          }
        </>}
      />

      <Card>
        <Table
          columns={tableColumns} data={filtered} loading={loading}
          onEdit={canEdit ? openEdit : undefined}
          onToggle={canEdit ? handleToggle : undefined}
          onDelete={canDelete ? handleDelete : undefined}
        />
      </Card>

      {/* ── Form Modal ── */}
      <Modal
        open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit ${entity}` : `New ${entity}`}
        subtitle={editing ? `Update ${entity.toLowerCase()} details` : `Fill in the details to create a new ${entity.toLowerCase()}`}
        width={500}
        footer={<>
          <Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={handleSave} loading={saving} icon={<Check size={13} />}>
            {editing ? `Update ${entity}` : `Add ${entity}`}
          </Btn>
        </>}
      >
        {(localError || saveError) && (
          <Alert
            message={localError || saveError}
            onClose={() => { setLocalError(""); clearError(); }}
          />
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {formFields.map(field => {
            const fieldStyle = field.full ? { gridColumn: "1/-1" } : {};
            return field.type === "select"
              ? <Select2
                key={field.key}
                label={field.label}
                value={String(form[field.key] ?? "")}
                onChange={setField(field.key)}
                options={field.options || []}
                required={field.required}
                placeholder={field.placeholder}
                hint={field.hint}
                style={fieldStyle}
              />
              : <Input
                key={field.key}
                label={field.label}
                value={form[field.key] ?? ""}
                onChange={setField(field.key)}
                placeholder={field.placeholder}
                required={field.required}
                type={field.type || "text"}
                hint={field.hint}
                style={fieldStyle}
              />;
          })}
        </div>
      </Modal>
    </div>
  );
}